import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/autoplay';

import { FC, useEffect, useMemo, useState } from 'react';
import { Autoplay, Pagination } from 'swiper';
import { Swiper as SwiperWrapper, SwiperSlide, useSwiper } from 'swiper/react';

import Banner from '@/components/elements/Banner';
import { Dummy } from '@/components/elements/Image';
import { useUserContext } from '@/contexts/user';
import useScript, { Status } from '@/hooks/useScript';
import { getSkyflagLink } from '@/lib/dmm/point';
import { BannerAreaLabel } from '@/lib/dmm/trackingTag/type';
import { getLink } from '@/lib/mainvisual';
import { Banner as BannerItem } from '@/lib/microcms/model';

import {
  BANNER_ASPECT_RATIO,
  Container,
  DummyItem,
  ListItem,
  NavigationButton,
} from './style';

interface MainVisualContentType {
  mainVisuals: BannerItem[];
  enteringCampaignIds: string[];
}

const NavigationBtn: FC<{
  iconType: 'next' | 'prev';
  slideWidth: string;
}> = ({ iconType, slideWidth }) => {
  const swiper = useSwiper();

  const handleSubmit = () => {
    switch (iconType) {
      case 'next': {
        swiper.slideNext();
        break;
      }
      case 'prev': {
        swiper.slidePrev();
        break;
      }
    }
  };
  return (
    <NavigationButton
      iconType={iconType}
      slideWidth={slideWidth}
      onClick={() => handleSubmit()}
    />
  );
};

const DUMMY_BANNER_COUNT = 4;

const MainVisual: FC<MainVisualContentType> = ({
  mainVisuals,
  enteringCampaignIds,
}) => {
  const [slideWidth, setSlideWidth] = useState<string>(
    `${window.outerWidth}px`,
  );
  const [bannerWidth, setBannerWidth] = useState<number>(0);
  const { user } = useUserContext();

  const gtaScript = useScript('/customGtaScript.js?20241120');

  // MEMO: Swiper.jsの複製ロジック上、初期表示が2番目のバナーになるため最後尾のバナーを先頭にもってくるようにしている
  const bannerItems = useMemo(() => {
    if (!mainVisuals.length) {
      return [];
    }
    const lastIndex = mainVisuals.length - 1;
    const lastBanner = mainVisuals[lastIndex];
    return [
      lastBanner,
      ...mainVisuals.slice(0, lastIndex).map(mv => {
        if (mv.news?.id == 'info_20220525') {
          mv.news = undefined;
          mv.linkPath = getSkyflagLink(user.userId);
        }
        return mv;
      }),
    ];
  }, [mainVisuals]);

  const isExternal = (item: BannerItem) =>
    !getLink(item).startsWith(import.meta.env.BASE_URL);

  useEffect(() => {
    if (gtaScript === Status.Ready) {
      mainVisuals.forEach(b => {
        if (b.alt && b.alt === 'DMMプレミアム新規登録30日間無料') {
          window.sendPremiumBannerViewEvent(BannerAreaLabel.MainVisual);
        }
      });
    }
  }, [gtaScript]);

  /** クリックイベント */
  const clickEvent = (alt?: string, id?: string) => {
    if (alt && id && gtaScript === Status.Ready) {
      if (alt === 'DMMプレミアム新規登録30日間無料') {
        // プレミアムバナーのイベント通知
        window.sendPremiumBannerClickEvent(BannerAreaLabel.MainVisual);
      } else {
        // 上記以外のイベントのクリック通知
        window.sendKeirinBannerClickEvent(BannerAreaLabel.MainVisual, id, alt);
      }
    }
  };

  const getSubBannerId = (item: BannerItem) => {
    if (item.campaign) {
      return item.campaign.id;
    }
    if (item.blog) {
      return item.blog.id;
    }
    if (item.news) {
      return item.news.id;
    }
    return item.id;
  };

  const simpleHash = (str: string): boolean => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      // hash = ((hash << 5) - hash) + char; // DJB2の標準的な計算
      hash = (hash * 31 + char) | 0; // ビット演算で32ビット整数に変換しつつ、乗算と加算
    }

    // ハッシュ値を0から1の間に正規化（負の数を扱うため、絶対値を使用）
    return Math.abs(hash) / 2147483648 < 0.5;
  };

  const mergeQueryParams = (
    urlString: string,
    paramsToMerge: Record<string, string>,
  ): string => {
    if (!urlString) return urlString;

    try {
      // 1. まず、そのまま絶対URLとして解析を試みる
      let url: URL;

      // 文字列が http から始まるかチェック
      const isAbsolute = urlString.trim().startsWith('http');

      if (isAbsolute) {
        // 絶対URLの場合は第2引数なしで作成
        url = new URL(urlString);
      } else {
        // 相対パスの場合は、ダミーのベースURLを使用して解析
        // これにより現在のドメイン（keirin.dmm.com）が混入するのを防ぐ
        url = new URL(urlString, 'https://temporary-dummy-base.com');
      }

      // 2. パラメータをマージ
      for (const [key, value] of Object.entries(paramsToMerge)) {
        url.searchParams.set(key, value);
      }

      // 3. 出力の切り分け
      if (isAbsolute) {
        // 元が絶対URLなら、そのまま返す（ドメインは維持される）
        return url.toString();
      } else {
        // 元が相対パスなら、ドメインを除いたパスとクエリだけを返す
        // これで "/campaign/..." のような形式が維持される
        return url.pathname + url.search;
      }
    } catch (e) {
      // 解析に失敗した場合は、最悪でも元のリンクにパラメータを力技で足すか、そのまま返す
      console.error('URL Merge Error:', e);
      return urlString;
    }
  };

  return (
    <Container
      count={bannerItems.length ? mainVisuals.length : DUMMY_BANNER_COUNT}
    >
      <SwiperWrapper
        slidesPerView={1.2}
        centeredSlides={true}
        spaceBetween={8}
        loop={true}
        loopedSlides={bannerItems.length + 1}
        pagination={{
          clickable: true,
        }}
        breakpoints={{
          375: {
            slidesPerView: 1.3,
            spaceBetween: 16,
          },
          1024: {
            slidesPerView: 'auto',
          },
        }}
        autoplay={{
          delay: 5000,
        }}
        modules={[Pagination, Autoplay]}
        onResize={swiper => {
          const style = window.getComputedStyle(swiper.slides[0]);
          setSlideWidth(style.width);
        }}
        onInit={swiper => {
          setBannerWidth(swiper.slides[0].clientWidth);
        }}
      >
        {bannerItems.length ? (
          <>
            {bannerItems
              .filter(item => (item.image ? true : false))
              .map(item => {
                var image = item.image;
                var link = getLink(item);
                var utm = {
                  utm_source: 'lotation_banner',
                  utm_medium: '',
                  utm_campaign: '',
                  utm_term: '',
                };
                if (item.campaign) {
                  utm.utm_medium = 'campaign';
                  utm.utm_term = item.campaign.id;
                } else if (item.news) {
                  utm.utm_medium = 'news';
                  utm.utm_term = item.news.id;
                } else if (item.blog) {
                  utm.utm_medium = 'blog';
                  utm.utm_term = item.blog.id;
                }
                if (item.image_B) {
                  utm.utm_campaign = 'ABtest_A';
                  if (simpleHash(item.id + user.userId)) {
                    utm.utm_campaign = 'ABtest_B';
                    image = item.image_B;
                  }
                }
                if (
                  item.image_entry &&
                  item.campaign &&
                  enteringCampaignIds.includes(item.campaign.id)
                ) {
                  image = item.image_entry;
                  utm.utm_campaign = 'entering';
                }
                link = link ? mergeQueryParams(link, utm) : link;
                return (
                  <SwiperSlide key={item.id}>
                    {({ isActive }) => (
                      <ListItem
                        isActive={isActive}
                        onClick={() => {
                          clickEvent(item.alt, getSubBannerId(item));
                        }}
                      >
                        <Banner
                          srcSet={`${image.url}?w=${bannerWidth}&dpr=1 1x, ${image.url}?w=${bannerWidth}&dpr=2 2x`}
                          mediaSet={[
                            {
                              srcSet: `${image.url}?w=${bannerWidth}&dpr=1&fm=webp 1x, ${image.url}?w=${bannerWidth}&dpr=2&fm=webp 2x`,
                              type: 'image/webp',
                            },
                          ]}
                          aspectRatio={BANNER_ASPECT_RATIO}
                          alt={item.alt}
                          bannerId={getSubBannerId(item)}
                          link={link}
                          external={isExternal(item)}
                          disabled={!link || !isActive}
                          scrollContainer=".swiper-wrapper"
                        />
                      </ListItem>
                    )}
                  </SwiperSlide>
                );
              })}
          </>
        ) : (
          <>
            {new Array(DUMMY_BANNER_COUNT).fill(0).map((_, i) => (
              <SwiperSlide key={`${i}-skelton`}>
                <DummyItem>
                  <Dummy />
                </DummyItem>
              </SwiperSlide>
            ))}
          </>
        )}
        <NavigationBtn iconType="next" slideWidth={slideWidth} />
        <NavigationBtn iconType="prev" slideWidth={slideWidth} />
      </SwiperWrapper>
    </Container>
  );
};

export default MainVisual;
